// Invitation acceptance page
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'

interface InvitationDetails {
  id: string
  email: string
  status: string | null
  expires_at: string
  role_id: string | null
  roles: {
    name: string
  } | null
}

export default function InvitePage() {
  const getToken = useGetToken()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSigningUp, setIsSigningUp] = useState(false)
  
  // Form fields
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  useEffect(() => {
    checkInvitation()
  }, [token])

  const checkInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }

    try {
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('invitations')
        .select('*, roles(name)')
        .eq('token', token)
        .single()

      if (error || !data) {
        setError('Invalid or expired invitation')
        setLoading(false)
        return
      }

      if (data.status !== 'pending') {
        setError('This invitation has already been used')
        setLoading(false)
        return
      }

      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired')
        setLoading(false)
        return
      }

      setInvitation(data)
      setLoading(false)
    } catch (err) {
      setError('Failed to verify invitation')
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!invitation || !token) return

    setIsSigningUp(true)
    setError(null)

    try {
      // Sign up the user
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
      })

      if (authError) {
        setError(authError.message)
        setIsSigningUp(false)
        return
      }

      if (!authData.user) {
        setError('Failed to create account')
        setIsSigningUp(false)
        return
      }

      // Create user profile
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: invitation.email,
          first_name: firstName,
          last_name: lastName,
          role_id: invitation.role_id,
        })

      if (profileError) {
        setError(profileError.message)
        setIsSigningUp(false)
        return
      }

      // Accept the invitation
      const { data: acceptData, error: acceptError } = await authenticatedClient
        .rpc('accept_invitation', {
          invitation_token: token,
          user_id: authData.user.id
        })

      if (acceptError || !acceptData) {
        console.error('Failed to accept invitation:', acceptError)
      }

      // Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      })

      if (signInError) {
        navigate('/login')
      } else {
        navigate('/dashboard')
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred')
      setIsSigningUp(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Link to="/login">
          <Button>Go to Login</Button>
        </Link>
      </div>
    )
  }

  if (!invitation) {
    return null
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">You're Invited!</h2>
        <p className="mt-2 text-gray-600">
          You've been invited to join Share Lattice as a <strong>{invitation.roles?.name}</strong>
        </p>
      </div>

      <form onSubmit={handleAcceptInvitation} className="space-y-4 max-w-md mx-auto">
        <div className="bg-gray-50 p-4 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Email:</strong> {invitation.email}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="John"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Doe"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Choose a Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder="••••••••"
          />
          <p className="text-sm text-gray-500">Minimum 6 characters</p>
        </div>

        <Button type="submit" className="w-full" disabled={isSigningUp}>
          {isSigningUp ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Account...
            </>
          ) : (
            'Accept Invitation & Create Account'
          )}
        </Button>

        <p className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:text-primary/80">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  )
}
